import Cookies from "js-cookie";
import { useCallback, useSyncExternalStore } from "react";
import { getCookieSnapshot, removeCookie as removeStoreCookie, setCookie as setStoreCookie, subscribeCookieStore } from "./cookie.store";
import { type CookieName } from "./cookie.utils";

type CookieOpts = Parameters<typeof Cookies.set>[2];

/**
 * Reads one {@link CookieName} via `js-cookie`, subscribed with `useSyncExternalStore`
 * ([docs](https://react.dev/reference/react/useSyncExternalStore)) so components re-render when
 * {@link setCookie} / {@link removeCookie} in `@/features/cookies/cookie.store` run. Set/remove callbacks use the same helpers.
 *
 * Use `T` to type the cookie string when present (missing cookies are still `undefined`).
 */
export function useCookie<T>(name: CookieName): {
  value: T | undefined;
  set: (value: T, options?: CookieOpts) => void;
  remove: (options?: CookieOpts) => void;
} {
  const subscribe = useCallback((onChange: () => void) => subscribeCookieStore(onChange), []);

  const getSnapshot = useCallback((): T | undefined => {
    return getCookieSnapshot<T>(name);
  }, [name]);

  const getServerSnapshot = useCallback((): T | undefined => {
    return getCookieSnapshot<T>(name);
  }, [name]);

  const value = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);

  const setCookie = useCallback((next: T, options?: CookieOpts) => {
    setStoreCookie(name, next, options);
  }, []);

  const removeCookie = useCallback((options?: CookieOpts) => {
    removeStoreCookie(name, options);
  }, []);

  return {
    value,
    set: setCookie,
    remove: removeCookie,
  };
}
